import { Link, useLocation } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from 'src/components/ui/sidebar';

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
}) {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { open } = useSidebar();
  const currentSessionId = new URLSearchParams(location.searchStr).get(
    'sessionId',
  );

  const getNavigationUrl = (url: string) => {
    const baseUrl = url.split('?')[0];
    if (
      !currentSessionId ||
      (baseUrl !== '/forecast' && baseUrl !== '/configuration')
    ) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}sessionId=${currentSessionId}`;
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) =>
          item.items ? (
            <Collapsible
              asChild
              className='group/collapsible'
              key={item.title}
              open={item.isActive}
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <Link to={getNavigationUrl(item.url)}>
                    <SidebarMenuButton
                      className={
                        location.pathname.includes(
                          `${item.url?.split('?').shift()}`,
                        ) && location.pathname.split('/').length === 2
                          ? 'rounded-md bg-item-hover text-primary-foreground hover:bg-item-hover'
                          : location.pathname.includes(
                                `${item.url?.split('?').shift()}`,
                              ) &&
                              location.pathname.split('/').length === 3 &&
                              !open
                            ? 'rounded-md bg-item-hover text-primary-foreground hover:bg-item-hover'
                            : 'hover:bg-item-hover'
                      }
                      tooltip={t(item.title)}
                    >
                      {item.icon && <item.icon className='size-4' />}
                      <span>{t(item.title)}</span>
                    </SidebarMenuButton>
                  </Link>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => (
                      <SidebarMenuSubItem key={t(subItem.title)}>
                        <SidebarMenuSubButton
                          asChild
                          className={
                            location.pathname.includes(
                              `${subItem.url?.split('?').shift()}`,
                            )
                              ? 'rounded-md bg-item-hover text-primary-foreground'
                              : 'hover:bg-item-hover'
                          }
                        >
                          <Link to={getNavigationUrl(subItem.url)}>
                            <span>{t(subItem.title)}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem
              className={
                location.pathname.includes(`${item.url?.split('?').shift()}`)
                  ? 'rounded-md bg-item-hover text-primary-foreground'
                  : ''
              }
              key={item.title}
            >
              <SidebarMenuButton asChild tooltip={t(item.title)}>
                <Link
                  className='active: flex items-center gap-1 pl-2 hover:bg-item-hover'
                  to={getNavigationUrl(item.url)}
                >
                  {item.icon && <item.icon />}
                  <span>{t(item.title)}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ),
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
